import { Children, isValidElement } from 'react';
import { mergeClasses } from 'minimal-shared/utils';

import { styled } from '@mui/material/styles';

import { carouselClasses } from './classes';
import { CarouselArrowBasicButtons } from './components';
import { CarouselSlide } from './components/carousel-slide';

import type { CarouselProps, CarouselOptions } from './types';

// ----------------------------------------------------------------------

export function Carousel({
  sx,
  carousel,
  children,
  slotProps,
  className,
  ...other
}: CarouselProps) {
  const { mainRef, options } = carousel;

  const axis = options?.axis ?? 'x';
  const slideSpacing = options?.slideSpacing ?? '0px';

  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button === 0) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 1) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const handleWheel = (e: React.WheelEvent) => {
    if (e.deltaX === 0) {
      e.stopPropagation();
    }
  };

  const renderChildren = () =>
    Children.map(children, (child) => {
      if (isValidElement(child)) {
        const reactChild = child as React.ReactElement<{ key?: React.Key }>;

        return (
          <CarouselSlide key={reactChild.key} options={carousel.options} sx={slotProps?.slide}>
            {child}
          </CarouselSlide>
        );
      }
      return null;
    });

  return (
    <CarouselRoot
      sx={sx}
      ref={mainRef}
      axis={axis}
      className={mergeClasses([carouselClasses.root, className])}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      onWheel={handleWheel}
      {...other}
    >
      <CarouselContainer
        axis={axis}
        slideSpacing={slideSpacing}
        className={carouselClasses.container}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        onWheel={handleWheel}
        sx={[
          (theme) => ({
            ...(carousel.pluginNames?.includes('autoHeight') && {
              alignItems: 'flex-start',
              transition: theme.transitions.create(['height'], {
                easing: theme.transitions.easing.easeInOut,
                duration: theme.transitions.duration.shorter,
              }),
            }),
          }),
          ...(Array.isArray(slotProps?.container)
            ? (slotProps?.container ?? [])
            : [slotProps?.container]),
        ]}
      >
        {renderChildren()}
      </CarouselContainer>

      <CarouselArrowBasicButtons {...carousel.arrows} options={carousel.options} sx={{ mt: 2 }} />
    </CarouselRoot>
  );
}

// ----------------------------------------------------------------------

const CarouselRoot = styled('div', {
  shouldForwardProp: (prop: string) => !['axis', 'sx'].includes(prop),
})<Pick<CarouselOptions, 'axis'>>(() => ({
  margin: 'auto',
  maxWidth: '100%',
  overflow: 'hidden',
  position: 'relative',
  '&:focus': {
    outline: 'none',
  },
  variants: [{ props: { axis: 'y' }, style: { height: '100%' } }],
}));

const CarouselContainer = styled('ul', {
  shouldForwardProp: (prop: string) => !['axis', 'slideSpacing', 'sx'].includes(prop),
})<Pick<CarouselOptions, 'axis' | 'slideSpacing'>>(({ slideSpacing }) => ({
  display: 'flex',
  backfaceVisibility: 'hidden',
  userSelect: 'none',
  cursor: 'grab',
  '&:active': {
    cursor: 'grabbing',
  },
  margin: 0,
  padding: 0,
  listStyle: 'none',
  position: 'relative',
  touchAction: 'pan-y pinch-zoom',
  variants: [
    {
      props: { axis: 'x' },
      style: {
        marginLeft: `calc(${slideSpacing} * -1)`,
        flexDirection: 'row',
        '& > *': {
          flexShrink: 0,
          width: '100%',
          position: 'relative',
        },
        '&::-webkit-scrollbar': {
          display: 'none'
        },
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        willChange: 'transform',
        transform: 'translate3d(0, 0, 0)',
        WebkitOverflowScrolling: 'touch',
      },
    },
    {
      props: { axis: 'y' },
      style: {
        height: '100%',
        flexDirection: 'column',
        touchAction: 'pan-x',
        marginTop: `calc(${slideSpacing} * -1)`,
      },
    },
  ],
}));
